// Shared domain types matching backend API response shapes.

// Backend now uses JsonStringEnumConverter, so enums are strings
export type TicketStatus = 'Open' | 'InProgress' | 'WaitingOnClient' | 'Resolved' | 'Closed';
export type TicketCategory = 'Support' | 'Sales';
export type TicketPriority = 'Low' | 'Normal' | 'High' | 'Urgent';
export type UserRole = 'Employee' | 'Manager' | 'Admin' | 'PartnersMng' | 'CatalogMng' | 'OrdersMng';
export type NoteType = 1 | 2; // Comment Chat
export type IntegrationType = 1 | 2; // EmailIngest EmailSend
export type MessageDirection = 1 | 2; // Inbound Outbound
export type DeliveryStatus = 1 | 2 | 3 | 4; // Received Sent Delivered Failed

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  'Open': 'Open',
  'InProgress': 'In Progress',
  'WaitingOnClient': 'Waiting on Client',
  'Resolved': 'Resolved',
  'Closed': 'Closed',
};
export const TICKET_CATEGORY_LABELS: Record<TicketCategory, string> = {
  'Support': 'Support',
  'Sales': 'Sales',
};
export const TICKET_PRIORITY_LABELS: Record<TicketPriority, string> = {
  'Low': 'Low',
  'Normal': 'Normal',
  'High': 'High',
  'Urgent': 'Urgent',
};
export const USER_ROLE_LABELS: Record<UserRole, string> = {
  Employee: 'Employee',
  Manager: 'Manager',
  Admin: 'Admin',
  PartnersMng: 'Partners Manager',
  CatalogMng: 'Catalog Manager',
  OrdersMng: 'Orders Manager',
};

export type LeadStatus = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8; // New Contacted Qualified Proposal Negotiation Won Lost Prospect
export type LeadPriority = 1 | 2 | 3 | 4; // Low Normal High Urgent

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  1: 'New', 2: 'Contacted', 3: 'Qualified', 4: 'Proposal', 5: 'Negotiation', 6: 'Won', 7: 'Lost', 8: 'Prospect',
};
export const LEAD_PRIORITY_LABELS: Record<LeadPriority, string> = {
  1: 'Low', 2: 'Normal', 3: 'High', 4: 'Urgent',
};

export interface Ticket {
  ticketId: string;
  internalId: string; // T040: FR-008 - Human-readable ticket ID (CR8-SP###)
  companyName?: string; // T040: FR-010 - Client company name for display
  accountId: string;
  subject: string;
  description?: string;
  category: TicketCategory;
  status: TicketStatus;
  priority: TicketPriority;
  ownerUserId: string | null;
  ownerDisplayName?: string | null; // T034: FR-027 owner display name
  assignedToUserId?: string | null;
  createdAt: string;
  updatedAt: string;
  clientInfo?: ClientInfo;
  customerResponded: boolean;
  isUnread?: boolean; // US2 (Feature 039): unread indicator
}

export interface Account {
  accountId: string;
  companyName: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  status: string;
  createdAt: string;
  modifiedAt: string;
  createdByUserId: string | null;
  contactCount: number;
  leadCount?: number;
  ticketCount?: number;
  defaultContactCreated?: boolean | null;
  warnings?: Array<{ code: string; message: string }> | null;
  syncPrompt?: SyncPrompt | null;
  associatedLeads?: LeadSummary[]; // Feature 017: Associated leads display
  primaryContact?: PrimaryContact | null; // T077: Primary contact from creation
  clientSegment?: 'BeautyProClinics' | 'DropshipperReseller' | 'Corporate' | 'BrandOwner' | 'InfluencerCreator' | 'Other' | null;
  websiteUrl?: string | null;
}

/**
 * T077: Primary contact data returned with account creation.
 * Feature: 022-client-ux-internal-ids - User Story 4
 */
export interface PrimaryContact {
  contactId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isAutoGeneratedIncomplete: boolean; // T077: Flag for incomplete auto-generated contacts
}

/**
 * Lightweight client information for display in related entity views.
 * Feature: 018-edit-client-association - Added contactName for primary contact display
 * Feature: 019-client-management-fixes - Added status field for filtering
 */
export interface ClientInfo {
  accountId: string;
  companyName: string | null;
  contactName?: string | null; // Primary contact person name
  email: string | null;
  phone: string | null;
  address: string | null;
  status: 'ACTIVE' | 'ARCHIVED'; // Feature 019: Status for filtering and display
  clientSegment?: string | null; // Feature 019: Client segmentation
}

/**
 * Summary of a lead for display in account view.
 * Feature: 017-leads-tickets-enhancements
 */
export interface LeadSummary {
  leadId: string;
  subject: string;
  status: string;
  createdAt: string;
}

export interface SyncPrompt {
  hasChanges: boolean;
  contactId: string | null;
  changes: SyncChange[] | null;
}

export interface SyncChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

/** @deprecated Use Account */
export interface Client {
  clientId: string;
  email: string | null;
  phone: string | null;
  displayName: string | null;
}

export interface AccountContact {
  accountContactId: string;
  accountId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string | null;
  idNumber: string | null;
  dateOfBirth: string | null;
  isEmployed: boolean;
  isDefault: boolean;
  isAutoGeneratedIncomplete?: boolean; // T077: Flag for incomplete auto-generated contacts
  status: string;
  createdAt: string;
  modifiedAt: string;
  createdByUserId: string | null;
  warnings?: { code: string; message: string }[] | null;
}

export interface MessageEvent {
  eventId: string;
  ticketId: string;
  clientId: string;
  direction: MessageDirection;
  senderType: number;
  senderRef: string;
  subject: string | null;
  bodyText: string;
  bodyHtml: string | null;
  deliveryStatus: DeliveryStatus;
  occurredAt: string;
}

export interface InternalNote {
  noteId: string;
  ticketId: string;
  authorUserId: string;
  noteType: NoteType;
  content: string;
  createdAt: string;
}

export interface LeadReply {
  eventId: string;
  leadId: string | null;
  direction: 'Inbound' | 'Outbound';
  senderType: number;
  senderRef: string;
  subject: string | null;
  bodyText: string;
  bodyHtml: string | null;
  deliveryStatus: 'Received' | 'Sent' | 'Delivered' | 'Failed';
  occurredAt: string;
  attachments?: MaterialAttachment[];
}

export interface ProspectReply {
  eventId: string;
  prospectId: string;
  direction: 'Inbound' | 'Outbound';
  senderType: number;
  senderRef: string;
  subject: string | null;
  bodyText: string;
  bodyHtml: string | null;
  deliveryStatus: 'Received' | 'Sent' | 'Delivered' | 'Failed';
  occurredAt: string;
}

export interface TicketReply {
  eventId: string;
  ticketId: string | null;
  direction: 'Inbound' | 'Outbound';
  senderType: number;
  senderRef: string;
  subject: string | null;
  bodyText: string;
  bodyHtml: string | null;
  deliveryStatus: 'Received' | 'Sent' | 'Delivered' | 'Failed';
  occurredAt: string;
  attachments?: MaterialAttachment[];
}

export interface LeadNote {
  noteId: string;
  leadId: string;
  authorUserId: string;
  noteType: NoteType;
  content: string;
  createdAt: string;
}

export interface User {
  userId: string;
  email: string;
  displayName: string;
  role: UserRole;
  roles: UserRole[];
  isActive: boolean;
  createdAt: string;
}

export interface PromptConfig {
  promptConfigId: string;
  key: string;
  description: string;
  template: string;
  isActive: boolean;
  version: number;
  updatedAt: string;
}

export interface IntegrationSetting {
  integrationSettingId: string;
  integrationType: IntegrationType;
  key: string;
  valueRef: string;
  isEnabled: boolean;
  updatedAt: string;
}

export interface SnapshotItem {
  snapshotId: string;
  metricKey: string;
  metricValue: number;
  dimensionsJson: string;
  windowStart: string;
  windowEnd: string;
  computedAt: string;
}

export interface TicketSummary {
  open: number;
  inProgress: number;
  waitingOnClient: number;
  resolved: number;
  closed: number;
  totalCreatedToday: number;
}

// T113: Dashboard metrics with server-side caching (Feature 022-client-ux-internal-ids)
export interface DashboardMetrics {
  openTicketsCount: number;
  openLeadsCount: number;
  avgResponseTimeHours: number;
  ticketsByPriority: Record<string, number>;
  leadsByStatus: Record<string, number>;
  recentActivity: RecentActivityItem[];
  computedAt: string;          // ISO 8601 timestamp when metrics were computed
  nextRefreshAt: string;        // ISO 8601 timestamp when metrics will be refreshed
}

// T010: Live KPI counts for dashboard summary tiles (Feature 034-dashboard-search-filter-owner-fixes)
// T016: Added three prospect pipeline fields (Feature 043-ui-refresh-dashboard-data)
export interface DashboardSummary {
  newTickets: number;
  newLeads: number;
  openTickets: number;
  openLeads: number;
  closedTicketsToday: number;
  closedLeadsToday: number;
  // Feature 043: prospect pipeline KPI tiles
  newProspectsToday: number;
  openProspects: number;
  closedProspectsToday: number;
}

export interface RecentActivityItem {
  type: 'Ticket' | 'Lead';
  id: string;
  internalId: string;           // CR8-SP### or CR8-L###
  subject: string;
  timestamp: string;
}

export interface PagedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface MarketingDocument {
  documentId: string;
  filename: string;
  fileType: string;
  fileSizeBytes: number;
  uploadedAt: string;
  uploaderName: string;
  contentType: string;
  headline: string | null;
  description: string | null;
  lastModifiedAt: string | null;
  isPublic: boolean;
}

export interface FileUploadResponse {
  documentId: string;
  filename: string;
  uploadedAt: string;
  headline: string | null;
  message: string;
}

export interface FileDownloadResponse {
  presignedUrl: string;
  expiresInSeconds: number;
}

export interface PublicMaterial {
  documentId: string;
  filename: string;
  fileType: string;
  fileSizeBytes: number;
  headline: string;
  description: string;
  thumbnailUrl: string | null;
  uploadedAt: string;
}

export interface MaterialsCatalogResponse {
  materials: PublicMaterial[];
  totalCount: number;
  retrievedAt: string;
}

export interface FileDownloadResponse {
  presignedUrl: string;
  expiresInSeconds: number;
}

export interface CreateReplyWithMaterialsRequest {
  bodyText: string;
  bodyHtml?: string;
  materialIds: string[];
}

export interface MaterialAttachment {
  attachmentId: string;
  sourceMaterialId: string;
  fileName: string;
  fileType: string;
  fileSizeBytes: number;
  attachedAt: string;
}

export interface ReplyWithAttachmentsResponse {
  eventId: string;
  ticketId: string;
  direction: string;
  senderType: string;
  senderRef: string;
  subject: string;
  bodyText: string;
  occurredAt: string;
  deliveryStatus: string;
  attachments: MaterialAttachment[];
}

export interface Lead {
  leadId: string;
  internalId: string; // T041: FR-008 - Human-readable lead ID (CR8-L###)
  companyName?: string; // T041: FR-010 - Client company name for display
  accountId: string;
  subject: string;
  description?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  status: string;
  priority: string;
  ownerUserId: string | null;
  ownerDisplayName?: string | null; // T033: FR-027 owner display name
  leadSource: string | null;
  convertedToTicketId: string | null;
  createdAt: string;
  updatedAt: string;
  qualifiedAt: string | null;
  closedAt: string | null;
  deletedAt: string | null;
  clientInfo?: ClientInfo;
  customerResponded: boolean;
  isUnread?: boolean; // US2 (Feature 039): unread indicator
  promotedAt?: string | null; // FR-001: non-null = lead is permanently read-only
}

export interface LeadListResponse {
  items: Lead[];
  totalCount: number;
  pageSize: number;
  pageNumber: number;
}

export interface ConversionResponse {
  sourceId: string;
  targetId: string;
  sourceType: string;
  targetType: string;
  convertedAt: string;
  message: string;
}

/** US1 (Feature 039): Client association search result. */
export interface AccountSearchResult {
  accountId: string;
  companyName: string | null;
  primaryContactEmail: string | null;
}
